// Customer Slack alerts — Messages → Settings → Slack alerts.
//
// "Add to Slack" connects the account's Slack workspace; the user then picks a
// channel and which messaging-failure alerts to receive. Hidden entirely until
// PadPulse's Slack app is configured on the server ({ available: false }).
//
// OAuth return: Slack redirects to /api/slack/oauth/callback, which forwards
// code+state to this page (session cookies are SameSite=strict and are not
// sent on Slack's cross-site redirect). This component POSTs them same-origin
// to /oauth/complete, where the session-bound state is verified.
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AlertTriangle, Check, ChevronsUpDown, Hash, Loader2, Lock, Send, Unplug } from "lucide-react";
import { format } from "date-fns";

export const SLACK_CONNECTION_QUERY_KEY = ["/api/app/slack-connection"] as const;

export type SlackAlertToggles = {
  failedMessages: boolean;
  stuckMessages: boolean;
  broadcastProblems: boolean;
};

export type SlackConnectionState =
  | { available: false }
  | { available: true; connected: false; status: null }
  | {
      available: true;
      connected: boolean;
      status: "active" | "disconnected";
      teamId: string;
      teamName: string | null;
      channel: { id: string; name: string | null; isPrivate: boolean } | null;
      alerts: SlackAlertToggles;
      lastError: string | null;
      lastErrorAt: string | null;
      lastPostedAt: string | null;
      connectedAt: string | null;
    };

type SlackChannel = { id: string; name: string; isPrivate: boolean; isMember: boolean };

const ALERT_OPTIONS: Array<{ key: keyof SlackAlertToggles; label: string; description: string }> = [
  {
    key: "failedMessages",
    label: "Failed member messages",
    description: "A message to a member couldn't be sent after every retry.",
  },
  {
    key: "stuckMessages",
    label: "Stuck or unconfirmed messages",
    description: "Messages are waiting too long to send, or weren't confirmed as delivered.",
  },
  {
    key: "broadcastProblems",
    label: "Broadcast problems",
    description: "A broadcast didn't reach everyone, or may have missed residents.",
  },
];

function formatWhen(value: string | null | undefined): string {
  if (!value) return "";
  try {
    return format(new Date(value), "MMM d, h:mm a");
  } catch {
    return "";
  }
}

/** Read and strip the OAuth return params once, before any render effects. */
function takeOAuthReturnParams(): { code: string; state: string } | { outcome: string } | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const code = params.get("slack_code");
  const state = params.get("slack_state");
  const outcome = params.get("slack");
  if (!code && !outcome) return null;
  params.delete("slack_code");
  params.delete("slack_state");
  params.delete("slack");
  const rest = params.toString();
  window.history.replaceState(null, "", `${window.location.pathname}${rest ? `?${rest}` : ""}`);
  if (code && state) return { code, state };
  return { outcome: outcome ?? "error" };
}

export function SlackAlertsSettings() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [oauthReturn] = useState(takeOAuthReturnParams);
  const completedRef = useRef(false);
  const [channelOpen, setChannelOpen] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  const connectionQuery = useQuery<SlackConnectionState>({ queryKey: SLACK_CONNECTION_QUERY_KEY });
  const conn = connectionQuery.data;
  const details = conn && conn.available && conn.status ? conn : null;
  const connected = !!details?.connected;

  const channelsQuery = useQuery<{ channels: SlackChannel[] }>({
    queryKey: ["/api/app/slack-connection/channels"],
    enabled: connected && channelOpen,
    staleTime: 60_000,
    retry: false,
  });

  const setConnection = (next: SlackConnectionState) => {
    queryClient.setQueryData(SLACK_CONNECTION_QUERY_KEY, next);
  };

  const installMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/slack-connection/install");
      return (await res.json()) as { url: string };
    },
    meta: { silentSave: true },
    onSuccess: ({ url }) => {
      window.location.assign(url);
    },
    onError: (err) => {
      toast({ title: "Couldn't start the Slack connection", description: parseErrorMessage(err, ""), variant: "destructive" });
    },
  });

  const completeMutation = useMutation({
    mutationFn: async (input: { code: string; state: string }) => {
      const res = await apiRequest("POST", "/api/app/slack-connection/oauth/complete", input);
      return (await res.json()) as SlackConnectionState;
    },
    meta: { silentSave: true },
    onSuccess: (next) => {
      setConnection(next);
      const team = next.available && next.status ? next.teamName : null;
      toast({ title: team ? `Connected to ${team}` : "Slack connected", description: "Now pick the channel for alerts." });
      setChannelOpen(true);
    },
    onError: (err) => {
      toast({ title: "Couldn't connect Slack", description: parseErrorMessage(err, ""), variant: "destructive" });
    },
  });

  useEffect(() => {
    if (!oauthReturn || completedRef.current) return;
    completedRef.current = true;
    if ("code" in oauthReturn) {
      completeMutation.mutate(oauthReturn);
    } else if (oauthReturn.outcome === "cancelled") {
      toast({ title: "Slack connection cancelled" });
    } else {
      toast({ title: "Couldn't connect Slack", description: "Slack didn't return an approval. Try again.", variant: "destructive" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oauthReturn]);

  const updateMutation = useMutation({
    mutationFn: async (body: { channelId?: string; alerts?: Partial<SlackAlertToggles> }) => {
      const res = await apiRequest("PATCH", "/api/app/slack-connection", body);
      return (await res.json()) as SlackConnectionState;
    },
    onSuccess: (next, body) => {
      setConnection(next);
      setTestResult(null);
      if (body.channelId && next.available && next.status && next.channel) {
        toast({ title: `Alerts will post to #${next.channel.name ?? next.channel.id}` });
      }
    },
    onError: (err) => {
      toast({ title: "Couldn't save Slack settings", description: parseErrorMessage(err, ""), variant: "destructive" });
      queryClient.invalidateQueries({ queryKey: SLACK_CONNECTION_QUERY_KEY });
    },
  });

  const testMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/slack-connection/test");
      return (await res.json()) as { ok: true; connection: SlackConnectionState };
    },
    meta: { silentSave: true },
    onSuccess: (data) => {
      setConnection(data.connection);
      setTestResult({ ok: true, message: "Test message posted. Check the channel in Slack." });
    },
    onError: (err) => {
      setTestResult({ ok: false, message: parseErrorMessage(err, "Slack didn't accept the test message.") });
      queryClient.invalidateQueries({ queryKey: SLACK_CONNECTION_QUERY_KEY });
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("DELETE", "/api/app/slack-connection");
      return (await res.json()) as SlackConnectionState;
    },
    onSuccess: (next) => {
      setConnection(next);
      setConfirmDisconnect(false);
      setTestResult(null);
      toast({ title: "Slack disconnected", description: "PadPulse will stop posting alerts to Slack." });
    },
    onError: (err) => {
      toast({ title: "Couldn't disconnect Slack", description: parseErrorMessage(err, ""), variant: "destructive" });
    },
  });

  const channels = channelsQuery.data?.channels ?? [];
  const privateChannelsMissing = useMemo(() => channels.every((c) => !c.isPrivate), [channels]);

  if (connectionQuery.isLoading) {
    return (
      <Card className="p-6 mb-6" data-testid="card-slack-alerts-loading">
        <Skeleton className="h-16 w-full" />
      </Card>
    );
  }
  // Hidden until the Slack app is configured, or when the query failed (e.g.
  // no messages access) — the rest of the page is unaffected.
  if (!conn || !conn.available) return null;

  const busy = installMutation.isPending || completeMutation.isPending;
  const disconnected = details?.status === "disconnected";

  return (
    <Card className="p-6 mb-6" data-tour="messages-settings-slack" data-testid="card-slack-alerts">
      <div className="flex items-start justify-between gap-4 mb-1">
        <div>
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            Slack alerts
            {connected && (
              <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100" data-testid="badge-slack-connected">
                Connected
              </Badge>
            )}
            {disconnected && (
              <Badge variant="destructive" data-testid="badge-slack-disconnected">
                Disconnected — reconnect
              </Badge>
            )}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Get a Slack message when a member message fails, gets stuck, or a broadcast doesn't reach everyone. Alerts
            also stay in your Team Inbox.
          </p>
        </div>
        {details && (
          <Button
            variant="ghost"
            size="sm"
            className="text-gray-500 shrink-0"
            onClick={() => setConfirmDisconnect(true)}
            data-testid="button-slack-disconnect"
          >
            <Unplug className="h-4 w-4 mr-1" />
            Disconnect
          </Button>
        )}
      </div>

      {!connected ? (
        <div className="mt-4 space-y-3">
          {disconnected && details?.lastError && (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="text-slack-disconnected-reason">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{details.lastError}</span>
            </div>
          )}
          <Button onClick={() => installMutation.mutate()} disabled={busy} data-testid="button-add-to-slack">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {disconnected ? "Reconnect Slack" : "Add to Slack"}
          </Button>
          <p className="text-xs text-gray-500">
            You'll approve the PadPulse app in Slack, then come back here to pick a channel.
          </p>
        </div>
      ) : (
        <div className="mt-4 space-y-5">
          <p className="text-sm text-gray-700" data-testid="text-slack-workspace">
            Workspace: <span className="font-medium">{details?.teamName ?? details?.teamId}</span>
          </p>

          <div>
            <Label className="text-sm font-medium text-gray-900">Channel</Label>
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              <Popover open={channelOpen} onOpenChange={setChannelOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-72 justify-between" data-testid="button-slack-channel-picker">
                    <span className="flex items-center gap-1.5 truncate">
                      {details?.channel ? (
                        <>
                          {details.channel.isPrivate ? <Lock className="h-3.5 w-3.5" /> : <Hash className="h-3.5 w-3.5" />}
                          {details.channel.name ?? details.channel.id}
                        </>
                      ) : (
                        <span className="text-gray-500">Pick a channel…</span>
                      )}
                    </span>
                    <ChevronsUpDown className="h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search channels…" data-testid="input-slack-channel-search" />
                    <CommandList>
                      {channelsQuery.isLoading ? (
                        <div className="p-3 text-sm text-gray-500 flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" /> Loading channels…
                        </div>
                      ) : channelsQuery.isError ? (
                        <div className="p-3 text-sm text-red-700" data-testid="text-slack-channels-error">
                          {parseErrorMessage(channelsQuery.error, "Couldn't load channels from Slack.")}
                        </div>
                      ) : (
                        <>
                          <CommandEmpty>No channels match.</CommandEmpty>
                          <CommandGroup>
                            {channels.map((channel) => (
                              <CommandItem
                                key={channel.id}
                                value={`${channel.name} ${channel.id}`}
                                onSelect={() => {
                                  setChannelOpen(false);
                                  if (channel.id !== details?.channel?.id) updateMutation.mutate({ channelId: channel.id });
                                }}
                                data-testid={`option-slack-channel-${channel.id}`}
                              >
                                {channel.isPrivate ? <Lock className="h-3.5 w-3.5 mr-1.5" /> : <Hash className="h-3.5 w-3.5 mr-1.5" />}
                                {channel.name}
                                {details?.channel?.id === channel.id && <Check className="ml-auto h-4 w-4" />}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </>
                      )}
                    </CommandList>
                    <div className="border-t p-2 text-xs text-gray-500">
                      {privateChannelsMissing ? "Private channel not listed? " : "Missing a private channel? "}
                      In Slack, open it and type <code className="rounded bg-gray-100 px-1">/invite @PadPulse</code>, then
                      reopen this list.
                    </div>
                  </Command>
                </PopoverContent>
              </Popover>
              {updateMutation.isPending && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
              <Button
                variant="outline"
                onClick={() => testMutation.mutate()}
                disabled={!details?.channel || testMutation.isPending}
                data-testid="button-slack-test"
              >
                {testMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                Send test message
              </Button>
            </div>
            {!details?.channel && (
              <p className="mt-1.5 text-xs text-amber-700" data-testid="text-slack-no-channel">
                Pick a channel — no alerts are posted until you do.
              </p>
            )}
            {testResult && (
              <p
                className={`mt-2 text-sm ${testResult.ok ? "text-emerald-700" : "text-red-700"}`}
                data-testid={testResult.ok ? "text-slack-test-success" : "text-slack-test-error"}
              >
                {testResult.message}
              </p>
            )}
            {!testResult && details?.lastError && (
              <div className="mt-2 flex items-start gap-2 text-sm text-red-700" data-testid="text-slack-last-error">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  {details.lastError}
                  {details.lastErrorAt ? ` (${formatWhen(details.lastErrorAt)})` : ""}
                </span>
              </div>
            )}
            {details?.lastPostedAt && !details.lastError && (
              <p className="mt-1.5 text-xs text-gray-500" data-testid="text-slack-last-posted">
                Last posted {formatWhen(details.lastPostedAt)}
              </p>
            )}
          </div>

          <div>
            <Label className="text-sm font-medium text-gray-900">Alerts to send</Label>
            <div className="mt-2 divide-y rounded-md border">
              {ALERT_OPTIONS.map((option) => (
                <div key={option.key} className="flex items-start justify-between gap-4 p-3">
                  <div>
                    <Label htmlFor={`slack-alert-${option.key}`} className="text-sm font-medium text-gray-900">
                      {option.label}
                    </Label>
                    <p className="text-xs text-gray-500 mt-0.5">{option.description}</p>
                  </div>
                  <Switch
                    id={`slack-alert-${option.key}`}
                    checked={details?.alerts[option.key] ?? true}
                    disabled={updateMutation.isPending}
                    onCheckedChange={(checked) => updateMutation.mutate({ alerts: { [option.key]: checked } })}
                    data-testid={`switch-slack-alert-${option.key}`}
                  />
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs text-gray-500">
              When many messages fail at once, PadPulse posts one summary instead of one alert per member. Recoveries
              are posted as replies in the same thread.
            </p>
          </div>
        </div>
      )}

      <Dialog open={confirmDisconnect} onOpenChange={(open) => !disconnectMutation.isPending && setConfirmDisconnect(open)}>
        <DialogContent
          onEscapeKeyDown={(e) => {
            if (disconnectMutation.isPending) e.preventDefault();
          }}
          onInteractOutside={(e) => {
            if (disconnectMutation.isPending) e.preventDefault();
          }}
          onPointerDownOutside={(e) => {
            if (disconnectMutation.isPending) e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>Disconnect Slack?</DialogTitle>
            <DialogDescription>
              PadPulse will stop posting alerts to Slack and remove its access to your workspace. Failure alerts keep
              appearing in your Team Inbox.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDisconnect(false)} data-testid="button-cancel-slack-disconnect">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => disconnectMutation.mutate()}
              disabled={disconnectMutation.isPending}
              data-testid="button-confirm-slack-disconnect"
            >
              {disconnectMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Disconnect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
